import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Snippet, VersionedSnippet, Version, VersionName } from './types';
import { getSortOrder } from './sort';

export const useActiveSnippet = <T extends Snippet>(snippets: T[], activeLanguage: string | undefined): T => {
  if (!snippets.length) {
    throw new Error('No Snippets provided');
  }

  const activeSnippet = useMemo(
    () => (activeLanguage === undefined ? snippets[0] : findSnippetByLanguage(snippets, activeLanguage)),
    [activeLanguage, snippets]
  );

  if (!activeSnippet) {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw new Error(`CodeSnippet doesn't support language ${activeLanguage}. Please specify snippet with such syntax in snippets property.`);
  }

  return activeSnippet;
};

export const useActiveSnippetVersion = (langVersion: string | undefined, activeSnippet: VersionedSnippet): Version => {
  const activeSnippetVersion = useMemo(
    () => (langVersion === undefined ? activeSnippet.versions[0] : findVersionInSnippet(activeSnippet, langVersion)),
    [activeSnippet, langVersion]
  );
  if (!activeSnippetVersion) {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw new Error(`CodeSnippet doesn't support version ${langVersion}. Please specify snippet with such syntax in snippets property.`);
  }

  return activeSnippetVersion;
};

export const useTabId = () => useMemo(uuidv4, []);

export const findSnippetByLanguage = <T extends Snippet>(snippets: T[], language: string) => snippets.find((snippet) => snippet.language === language);

export const findVersionInSnippet = <T extends VersionedSnippet>(activeSnippet: T, langVersion: VersionName) =>
  activeSnippet.versions.find((version) => version.versionName === langVersion);

export const useSortSnippets = <T extends Snippet>(snippets: T[]): T[] => {
  return [...snippets].sort((a, b) => getSortOrder(a.language) - getSortOrder(b.language));
};
